// Copied from platform-app pages/pos/PosReceiptEditor.tsx on 2026-10-14 for Super Admin → POS Defaults — keep in step with the original.
import React, { useCallback, useEffect, useState } from 'react';
import { Printer, Usb, Save, Image as ImageIcon, Building2 } from 'lucide-react';
// POS Defaults: loads/saves the GLOBAL receipt default through super-admin-ext2
// (`api.posDefaults`) instead of a location's receipt config. No logo upload,
// no business-details editing, no "Copy to locations", no reset (the default
// has no parent to fall back to). Preview + Test Print are unchanged.
import { api } from '../../../../services/apiService';
import { toastService } from '../../../../services/toastService';
import type { PrinterReceiptConfig, ReceiptBlock } from '@platform/shared-types';
import SlotEditor from '../../components/pos/receipt/SlotEditor';
import ReceiptPreview from '../../components/pos/receipt/ReceiptPreview';
import { MAX_TOTAL_BLOCKS } from '../../components/pos/receipt/blockDefaults';
import { buildReceiptHtml, printToBrowser } from '../../components/pos/receipt/printToBrowser';
import { encodeReceipt } from '../../components/pos/receipt/escpos';
import {
  isWebUsbSupported,
  tryRestoreDevice,
  requestDevice as requestUsbDevice,
  sendBytes as sendUsbBytes,
  getCachedDevice,
} from '../../components/pos/receipt/webUsbPrinter';
import { RubyLoader, RubyGemMark } from '../../components/RubyLoader';

// Sample order used by the live preview AND the Test Print button —
// includes every line type a real receipt can show so users can verify layout.
const SAMPLE_ORDER = {
  legalName: 'Sample Location LLC',
  address: '123 Demo St, Demo City IL 60601',
  phone: '(555) 123-4567',
  orderNumber: 'TEST-0001',
  timestamp: Date.now(),
  cashier: 'Test Cashier',
  items: [
    { qty: 2, name: 'Latte (12oz)', price: 4.50 },
    { qty: 1, name: 'Bagel w/ Cream Cheese', price: 3.25 },
    { qty: 1, name: 'Cold Brew (16oz)', price: 5.25 },
  ],
  subtotal: 18.00,
  tax: 1.26,
  fees: [{ label: 'Bag fee', amount: 0.10 }],
  surcharges: [{ label: 'CC surcharge (3%)', amount: 0.58 }],
  tip: 3.00,
  total: 22.94,
  payment: 'Visa ****4242',
};

export interface PosDefaultsMeta {
  updatedAt: number | null;
  updatedBy: string | null;
  source: 'stored' | 'code';
}

interface Props {
  /** Reports last-saved info to the page header after load and after each save. */
  onMeta?: (meta: PosDefaultsMeta) => void;
}

/** The shape the PUT takes — the default carries no tenant/location/logo. */
const empty = (): PrinterReceiptConfig => ({
  tenantId: '', locationId: '', logoUrl: null, printerWidth: '80mm',
  businessDetailsOverride: null,
  slots: { header: [], belowItems: [], footer: [] },
  version: 0, updatedAt: 0, updatedBy: '',
} as PrinterReceiptConfig);

export default function PosReceiptEditor({ onMeta }: Props) {
  const [config, setConfig] = useState<PrinterReceiptConfig | null>(null);
  const [savedJson, setSavedJson] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // POS Defaults: the app's own toast service replaces the page-local toast.
  const setToast = (t: { kind: 'ok' | 'err'; text: string }) =>
    t.kind === 'ok' ? toastService.success(t.text) : toastService.error(t.text);
  const [previewWidth, setPreviewWidth] = useState<'80mm' | '58mm'>('80mm');
  const [usbConnected, setUsbConnected] = useState<boolean>(!!getCachedDevice());

  // On mount, try to silently restore a previously-paired USB printer.
  useEffect(() => {
    if (!isWebUsbSupported()) return;
    let cancelled = false;
    void tryRestoreDevice().then((d) => { if (!cancelled && d) setUsbConnected(true); });
    return () => { cancelled = true; };
  }, []);

  const handleConnectPrinter = async () => {
    try {
      await requestUsbDevice();
      setUsbConnected(true);
      setToast({ kind: 'ok', text: 'Printer connected' });
    } catch (err: any) {
      // User cancelled the picker — silently no-op
      if (err?.name === 'NotFoundError' || /No device selected/i.test(String(err?.message))) return;
      console.error('[usb] connect failed', err);
      setToast({ kind: 'err', text: err?.message || 'Could not connect printer' });
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.posDefaults.getReceipt();
      const cfg = { ...empty(), ...(r?.config || {}) } as PrinterReceiptConfig;
      cfg.slots = { header: [], belowItems: [], footer: [], ...(r?.config?.slots || {}) };
      cfg.logoUrl = null;
      cfg.businessDetailsOverride = null;
      setConfig(cfg);
      setSavedJson(JSON.stringify({ printerWidth: cfg.printerWidth, slots: cfg.slots }));
      onMeta?.({ updatedAt: r?.updatedAt ?? null, updatedBy: r?.updatedBy ?? null, source: r?.source === 'stored' ? 'stored' : 'code' });
    } catch (err) {
      console.error('[receipt default] load failed', err);
      setConfig(empty());
      setToast({ kind: 'err', text: 'Failed to load the receipt default — showing an empty one' });
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalBlocks = config
    ? config.slots.header.length + config.slots.belowItems.length + config.slots.footer.length
    : 0;

  const updateSlot = (key: 'header' | 'belowItems' | 'footer') => (next: ReceiptBlock[]) => {
    if (!config) return;
    setConfig({ ...config, slots: { ...config.slots, [key]: next } });
  };

  const dirty = !!config && JSON.stringify({ printerWidth: config.printerWidth, slots: config.slots }) !== savedJson;

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const body = {
        logoUrl: null,
        printerWidth: config.printerWidth,
        businessDetailsOverride: null,
        slots: config.slots,
      };
      const r = await api.posDefaults.putReceipt(body);
      setSavedJson(JSON.stringify({ printerWidth: config.printerWidth, slots: config.slots }));
      onMeta?.({ updatedAt: r?.updatedAt ?? Date.now(), updatedBy: r?.updatedBy ?? null, source: 'stored' });
      setToast({ kind: 'ok', text: 'Receipt default saved — every business without its own receipt now uses it' });
    } catch (err: any) {
      console.error('[receipt default] save failed', err);
      setToast({ kind: 'err', text: err?.message ? `Save failed — ${err.message}` : 'Save failed — please retry' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestPrint = async () => {
    if (!config) return;
    try {
      // Prefer USB if connected — silent, no popup, true thermal output
      if (usbConnected || getCachedDevice()) {
        const bytes = await encodeReceipt(config, SAMPLE_ORDER, previewWidth);
        await sendUsbBytes(bytes);
        setToast({ kind: 'ok', text: 'Sent to printer via USB' });
        return;
      }
      // Fallback: browser print dialog
      const html = await buildReceiptHtml(config, SAMPLE_ORDER, previewWidth);
      await printToBrowser(html);
    } catch (err: any) {
      console.error('[receipt] test print failed', err);
      setToast({ kind: 'err', text: err?.message || 'Test print failed — check console' });
    }
  };

  if (loading || !config) {
    return <RubyLoader variant="page" />;
  }

  return (
    <div>
      {/* POS Defaults: the page owns the black header, so the editor's own
          header becomes a toolbar row (same buttons minus Reset / Copy). */}
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200/60 px-4 py-3 mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Printer size={20} className="text-stone-700" />
          <div>
            <h2 className="text-sm font-semibold text-stone-800">Printer Receipt — RubyTill default</h2>
            <p className="text-[11px] text-stone-500">Used by every location that hasn&apos;t saved its own receipt</p>
          </div>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
          {isWebUsbSupported() && !usbConnected && (
            <button
              type="button"
              onClick={handleConnectPrinter}
              className="bg-white border border-stone-300 text-stone-800 px-3 min-h-[44px] rounded-lg text-sm font-medium hover:bg-stone-50 inline-flex items-center gap-1.5"
              title="Pair the USB thermal printer once — silent printing thereafter"
            >
              <Usb size={16} />
              Connect Printer
            </button>
          )}
          <button
            type="button"
            onClick={handleTestPrint}
            className="bg-white border border-stone-300 text-stone-800 px-3 min-h-[44px] rounded-lg text-sm font-medium hover:bg-stone-50 inline-flex items-center gap-1.5"
            title={usbConnected
              ? `Send a sample receipt directly to USB printer (${previewWidth})`
              : `Send a sample receipt via browser print at ${previewWidth}`}
          >
            <Printer size={16} />
            Test Print {usbConnected && <span className="text-[10px] text-accent-700 font-bold">USB</span>}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !dirty}
            className="bg-accent-600 text-white px-4 min-h-[44px] rounded-lg text-sm font-semibold hover:bg-accent-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            title={dirty ? 'Save the receipt default' : 'No unsaved changes'}
          >
            {saving ? <RubyGemMark size={15} drawing color="currentColor" /> : <Save size={15} />}
            Save default
          </button>
        </div>
      </div>

      <div className="text-stone-800">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_minmax(280px,360px)] gap-4 max-w-7xl">
          {/* Editor */}
          <div className="space-y-3">
            <div className="bg-white rounded-xl border border-stone-200 p-3 flex items-start gap-2 text-xs text-stone-500">
              <ImageIcon size={16} className="text-stone-400 shrink-0 mt-0.5" />
              <span>Each business&apos;s own logo prints wherever a <strong className="text-stone-700">Logo</strong> block sits — the default never carries one.</span>
            </div>

            <SlotEditor
              title="Header" subtitle="above order details"
              blocks={config.slots.header}
              onChange={updateSlot('header')}
              totalBlocksAcrossSlots={totalBlocks}
              maxBlocks={MAX_TOTAL_BLOCKS}
            />
            <div className="bg-stone-100 border border-dashed border-stone-300 rounded-xl p-3 text-xs text-stone-500 flex items-start gap-2">
              <Building2 size={14} className="shrink-0 mt-0.5" />
              <span>Business name · Address · Phone — each business&apos;s own name/address prints here</span>
            </div>
            <div className="bg-stone-100 border border-dashed border-stone-300 rounded-xl p-3 text-xs text-stone-500">🔒 Order # · Date · Cashier · Item list — legally required · always shown</div>
            <SlotEditor
              title="Below Items" subtitle="between item list and totals"
              blocks={config.slots.belowItems}
              onChange={updateSlot('belowItems')}
              totalBlocksAcrossSlots={totalBlocks}
              maxBlocks={MAX_TOTAL_BLOCKS}
            />
            <div className="bg-stone-100 border border-dashed border-stone-300 rounded-xl p-3 text-xs text-stone-500">🔒 Subtotal · Taxes · Fees · Surcharges · Total — legally required · always shown</div>
            <SlotEditor
              title="Footer" subtitle="bottom of receipt"
              blocks={config.slots.footer}
              onChange={updateSlot('footer')}
              totalBlocksAcrossSlots={totalBlocks}
              maxBlocks={MAX_TOTAL_BLOCKS}
            />
            <div className="text-[11px] text-stone-500">{totalBlocks}/{MAX_TOTAL_BLOCKS} blocks used</div>
          </div>

          {/* Preview */}
          <div className="lg:sticky lg:top-24 self-start space-y-3">
            <div className="bg-white rounded-2xl border border-stone-200 p-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-stone-700">Preview width</span>
              <div className="flex bg-stone-100 rounded-lg p-1 text-xs">
                <button type="button" onClick={() => setPreviewWidth('80mm')} className={`px-3 py-1 rounded-md ${previewWidth === '80mm' ? 'bg-white shadow-sm font-semibold' : 'text-stone-500'}`}>80mm</button>
                <button type="button" onClick={() => setPreviewWidth('58mm')} className={`px-3 py-1 rounded-md ${previewWidth === '58mm' ? 'bg-white shadow-sm font-semibold' : 'text-stone-500'}`}>58mm</button>
              </div>
            </div>
            <div className="bg-stone-200 rounded-xl p-4 flex justify-center">
              <ReceiptPreview config={config} order={SAMPLE_ORDER} width={previewWidth} />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
